import { useState, useMemo } from 'react';
import {
  Bug,
  Copy,
  Check,
  X,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Search,
  AlertCircle,
  AlertTriangle,
  Info,
  Trash2,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useConsoleError } from '@/hooks/useConsoleError';
import {
  getConsoleMessages,
  groupErrors,
  getErrorSummary,
  type GroupedError,
  type ErrorSeverity,
} from '@/lib/consoleMessages';

interface ConsolePanelProps {
  onRequestFix?: (errorMessage: string) => void;
  onRefresh?: () => void;
}

type FilterType = 'all' | 'critical' | 'warning' | 'info';

function SeverityIcon({ severity, className }: { severity: ErrorSeverity; className?: string }) {
  switch (severity) {
    case 'critical':
      return <AlertCircle className={cn('text-destructive', className)} />;
    case 'warning':
      return <AlertTriangle className={cn('text-amber-500', className)} />;
    case 'info':
      return <Info className={cn('text-blue-500', className)} />;
  }
}

function ErrorGroup({
  group,
  onRequestFix,
  onCopy,
  copiedKey,
}: {
  group: GroupedError;
  onRequestFix?: (errorMessage: string) => void;
  onCopy: (key: string, text: string) => void;
  copiedKey: string | null;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const firstError = group.errors[0];
  const { category, plainEnglish, technicalDetails, possibleCause, suggestedFix } = firstError;

  const timeAgo = useMemo(() => {
    const seconds = Math.floor((Date.now() - group.lastSeen) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  }, [group.lastSeen]);

  return (
    <div className={cn(
      'rounded-lg border transition-colors',
      category.severity === 'critical' && 'border-destructive/30 bg-destructive/5',
      category.severity === 'warning' && 'border-amber-500/30 bg-amber-500/5',
      category.severity === 'info' && 'border-blue-500/20 bg-blue-500/5',
    )}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button className="w-full p-3 text-left hover:bg-muted/50 transition-colors rounded-lg">
            <div className="flex items-start gap-3">
              <SeverityIcon severity={category.severity} className="h-4 w-4 mt-0.5 flex-shrink-0" />

              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{plainEnglish}</span>
                  {group.count > 1 && (
                    <Badge variant="secondary" className="text-xs px-1.5 py-0">
                      ×{group.count}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline" className="text-xs px-1.5 py-0 font-normal">
                    {category.name}
                  </Badge>
                  <span>{timeAgo}</span>
                </div>
              </div>

              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-3 pb-3 space-y-3">
            {/* Possible cause */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">Why this might happen:</div>
              <p className="text-sm">{possibleCause}</p>
            </div>

            {/* Suggested fix */}
            <div className="space-y-1">
              <div className="text-xs font-medium text-muted-foreground">How to fix it:</div>
              <p className="text-sm">{suggestedFix}</p>
            </div>

            {/* Technical details (collapsible) */}
            <Collapsible>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 px-2">
                  <ChevronRight className="h-3 w-3" />
                  Show technical details
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="relative mt-2">
                  <pre className="text-xs bg-muted/50 p-2 rounded-md overflow-x-auto whitespace-pre-wrap break-all max-h-40 font-mono">
                    {technicalDetails}
                  </pre>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCopy(group.key, technicalDetails);
                    }}
                    className="absolute top-1 right-1 h-6 w-6 p-0"
                  >
                    {copiedKey === group.key ? (
                      <Check className="h-3 w-3 text-green-500" />
                    ) : (
                      <Copy className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Action buttons */}
            {onRequestFix && (
              <div className="pt-2 border-t">
                <Button
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRequestFix(technicalDetails);
                  }}
                  className="h-8 text-xs gap-1.5"
                >
                  <Sparkles className="h-3 w-3" />
                  Ask AI to fix this
                </Button>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

export function ConsolePanel({ onRequestFix, onRefresh }: ConsolePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const { hasErrors, clearErrors } = useConsoleError();

  // Get messages and compute derived data only when popover is open
  const messages = isOpen ? getConsoleMessages() : [];
  const groupedErrors = useMemo(() => groupErrors(messages), [messages]);
  const summary = useMemo(() => getErrorSummary(messages), [messages]);

  // Filter groups based on search and severity filter
  const filteredGroups = useMemo(() => {
    return groupedErrors.filter(group => {
      const firstError = group.errors[0];

      // Apply severity filter
      if (activeFilter !== 'all' && firstError.category.severity !== activeFilter) {
        return false;
      }

      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          firstError.plainEnglish.toLowerCase().includes(query) ||
          firstError.technicalDetails.toLowerCase().includes(query) ||
          firstError.category.name.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [groupedErrors, activeFilter, searchQuery]);

  const handleCopy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleRequestFix = (errorMessage: string) => {
    onRequestFix?.(errorMessage);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 relative"
        >
          <Bug className="h-4 w-4" />
          {hasErrors && (
            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background bg-red-500" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[calc(100vw-1rem)] max-w-[520px] max-h-[70vh] overflow-hidden shadow-lg border rounded-xl bg-background p-0"
        sideOffset={4}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b bg-muted/30">
          <div className="flex items-center gap-2">
            <Bug className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium text-sm">Debug Console</span>
            {summary.total > 0 && (
              <Badge variant="secondary" className="text-xs">
                {summary.total} message{summary.total !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {onRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onRefresh();
                  setIsOpen(false);
                }}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                title="Refresh preview and clear messages"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            )}
            {summary.total > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearErrors}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                title="Clear all messages"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="h-7 w-7 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Summary badges */}
        {summary.total > 0 && (
          <div className="flex items-center gap-2 p-3 border-b overflow-x-auto">
            <div className="flex items-center gap-1.5">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <button
              onClick={() => setActiveFilter('all')}
              className={cn(
                'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                activeFilter === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              )}
            >
              All ({summary.total})
            </button>
            {summary.critical > 0 && (
              <button
                onClick={() => setActiveFilter('critical')}
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                  activeFilter === 'critical'
                    ? 'bg-destructive text-destructive-foreground'
                    : 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                )}
              >
                <AlertCircle className="h-3 w-3" />
                {summary.critical} Error{summary.critical !== 1 ? 's' : ''}
              </button>
            )}
            {summary.warnings > 0 && (
              <button
                onClick={() => setActiveFilter('warning')}
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                  activeFilter === 'warning'
                    ? 'bg-amber-500 text-white'
                    : 'bg-amber-500/10 text-amber-600 hover:bg-amber-500/20'
                )}
              >
                <AlertTriangle className="h-3 w-3" />
                {summary.warnings} Warning{summary.warnings !== 1 ? 's' : ''}
              </button>
            )}
            {summary.info > 0 && (
              <button
                onClick={() => setActiveFilter('info')}
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
                  activeFilter === 'info'
                    ? 'bg-blue-500 text-white'
                    : 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20'
                )}
              >
                <Info className="h-3 w-3" />
                {summary.info} Info
              </button>
            )}
          </div>
        )}

        {/* Search */}
        {summary.total > 3 && (
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 text-sm"
              />
            </div>
          </div>
        )}

        {/* Messages list */}
        <div className="max-h-[400px] overflow-y-auto p-3 space-y-2">
          {summary.total === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <Bug className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">No console messages</p>
              <p className="text-xs text-muted-foreground mt-1">
                Messages from your project will appear here
              </p>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-sm text-muted-foreground">No matching messages</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setActiveFilter('all');
                }}
                className="mt-2 text-xs"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            filteredGroups.map((group) => (
              <ErrorGroup
                key={group.key}
                group={group}
                onRequestFix={onRequestFix ? handleRequestFix : undefined}
                onCopy={handleCopy}
                copiedKey={copiedKey}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
